import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ActivityDetailItem, ActivityDetailResponse, ActivityTabBarResponse } from '@star/types';
import { NetworkTypeEnum } from '@star/constants';
import useActivityServices from '../services/activity';

type ActivityDataMap = Record<number, ActivityDetailResponse>;

const getActiveStatue = (item: any) => {
  const { showStartTime = '', showEndTime = '', startTime = '', endTime = '' } = item || {};
  const opt = [
    { label: '已失效', value: 0 },
    { label: '未开始', value: 1 },
    { label: '已结束', value: 2 },
    { label: '进行中', value: 3 },
  ];
  const now = Date.now();

  // 检查展示时间
  if (showStartTime && showEndTime) {
    const showStart = new Date(showStartTime).getTime();
    const showEnd = new Date(showEndTime).getTime();

    if (now < showStart || now > showEnd) {
      return opt[0]; // 已失效 不展示
    } else {
      // 当前时间在展示时间内
      if (startTime && endTime) {
        const start = new Date(startTime).getTime();
        const end = new Date(endTime).getTime();

        if (now < start) {
          return opt[1]; // 未开始
        } else if (now > end) {
          return opt[2]; // 已结束
        } else {
          return opt[3]; // 进行中
        }
      } else {
        // 如果没有提供生效时间，默认为进行中
        return opt[3]; // 进行中
      }
    }
  }

  // 如果没有提供展示时间，默认为进行中
  return opt[3]; // 进行中
};

// 活动Store
export const useActivityStore = defineStore('activity', () => {
  const { activityServices } = useActivityServices();

  // 索引
  const activeTab = ref(0);
  const activeTabVO = ref(0);
  // 活动tabbar数据
  const activityTabBarList = ref<ActivityTabBarResponse>([]);
  const activityTabBarListVO = ref<ActivityTabBarResponse>([]);
  // 活动tabar子项数据
  const activityDataMap = ref<ActivityDataMap>({});
  const activityDataMapVO = ref<ActivityDataMap>({});
  // 活动详情
  const activityContent = ref<any>({});
  const isLoading = ref(false);

  const waitReceiveNum = ref<number>(0); // 待领取/未读数字

  const rankDetail = ref({
    data: [],
    t: {},
  });

  const changeActiveTabVO = (index: number) => {
    activeTabVO.value = index;
    window.scrollTo(0, 0);

    // 数据请求
    // getActivityDetailVO();
    getCateActiveList();
  };

  // 获取活动菜单数据
  const getActivityDataVO = async () => {
    const res = await activityServices.getActivityDataVO();

    if (res.type == NetworkTypeEnum.SUCCESS) {
      activityTabBarListVO.value = res.data;
      activityTabBarListVO.value.unshift({
        cat: '全部活动',
        id: 0,
        orderNum: -1,
        categoryName: '',
        categoryPic: '',
        categoryType: '',
        status: '',
        tid: 0,
        create_time: 0,
        sort_v: 0,
        state_time: 0,
      });
    }
  };

  // 获取数据
  const getActivityDetailVO = async (forceRequest: boolean = false) => {
    const tabNum = activeTabVO.value;
    // 防重复请求
    if (!!activityDataMapVO.value[tabNum] && activityDataMapVO.value[tabNum].length && !forceRequest) {
      return;
    }

    if (!activityTabBarListVO.value.length) {
      return;
    }

    try {
      isLoading.value = true;
      const cat = activityTabBarListVO.value[tabNum].cat;
      const res = await activityServices.getActivityDetailVO(cat);

      if (res.type == NetworkTypeEnum.SUCCESS) {
        /** 获取每个活动的详情，content有aid则根据aid获取活动详情 */
        /**
         * 1. 已结束的活动，放到正在进行中的活动下面
         * 2. 直到活动展示结束时间到期，活动入口才在活动列表消失.
         * 3. 活动结束时间到期时，展示结束时间还未到期时，活动封面展示活动已结束字样，且不可点击查看详情。
         */
        // 活动id 7715  活动详情id 87
        // 1. 判断活动状态
        for (let i = 0; i < res.data.length; i++) {
          const item = res.data[i];
          const { elotActivityPg } = item;
          if (elotActivityPg) {
            res.data[i].detail = {
              statue: getActiveStatue(elotActivityPg).value,
            };
          }
        }
        // 2. 排序，已结束的放在最后
        res.data.sort((a, b) => {
          if (a.detail?.statue === 2 && b.detail?.statue !== 2) {
            return 1;
          } else if (a.detail?.statue !== 2 && b.detail?.statue === 2) {
            return -1;
          } else {
            return 0;
          }
        });
        // 3. 过滤不展示的活动
        const resData = res.data.filter(item => {
          return item.detail?.statue !== 0;
        });
        console.log('activityDataMapVO', resData);
        activityDataMapVO.value[tabNum] = resData;
      }
    } finally {
      isLoading.value = false;
    }
  };

  const changeActiveTab = (index: number) => {
    activeTab.value = index;
    window.scrollTo(0, 0);

    // 数据请求
    getActivityDetail();
  };

  const setActivityItem = (list: Array<ActivityDetailItem>) => {
    /** 获取每个活动的详情，content有aid则根据aid获取活动详情 */
    /**
     * 1. 已结束的活动，放到正在进行中的活动下面
     * 2. 直到活动展示结束时间到期，活动入口才在活动列表消失.
     * 3. 活动结束时间到期时，展示结束时间还未到期时，活动封面展示活动已结束字样，且不可点击查看详情。
     */
    // 活动id 7715  活动详情id 87
    // 1. 判断活动状态
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const { elotActivityPg } = item;
      if (item) {
        list[i].detail = {
          statue: getActiveStatue(elotActivityPg).value,
        };
      }
    }
    // 2. 排序，已结束的放在最后
    list.sort((a, b) => {
      if (a.detail?.statue === 2 && b.detail?.statue !== 2) {
        return 1;
      } else if (a.detail?.statue !== 2 && b.detail?.statue === 2) {
        return -1;
      } else {
        return 0;
      }
    });
    // 3. 过滤不展示的活动
    const resData = list.filter(item => {
      return item.detail?.statue !== 0;
    });

    console.log('setActivityItem---->', resData);
    return resData;
  };

  // 点击分类-获取分类下的活动列表
  const getCateActiveList = async (forceRequest: boolean = false) => {
    const tabNum = activeTabVO.value; // 分类id
    console.log('开始getCateActiveList--->', activeTabVO.value);
    try {
      isLoading.value = true;
      let categoryName = activityTabBarList.value[tabNum].categoryName;
      let typeId = activityTabBarList.value[tabNum].id;
      console.log('typeId--->', typeId);
      const res = await activityServices.getActivityDetailVO(categoryName, typeId);
      console.log('getCateActiveList--->', res);
      if (res.type == NetworkTypeEnum.SUCCESS) {
        // @ts-ignore
        activityDataMapVO.value[tabNum] = setActivityItem(res.data);
      }
    } finally {
      isLoading.value = false;
    }
  };

  // 获取活动菜单数据
  const getActivityData = async () => {
    const res = await activityServices.getActivityData({
      page_start: 1,
      num_per_page: 50,
    });

    console.log('左侧菜单数据------res--->', res);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      // @ts-ignore
      // activityTabBarList.value = res.data.data.filter(item => item.status == 1).sort((a, b) => a.orderNum - b.orderNum);
      activityTabBarList.value = res.data.data.filter(item => item.status == 1);

      // 不用这个了，开始说用这个接口下面的，临时改成点击后再调用接口。说后面会改回来。所以保留
      // activityTabBarList.value = activityTabBarList.value.map(item => {
      //   if (item.activityPgList) {
      //     return {
      //   ...item,
      //   activityPgList: setActivityItem(item.activityPgList),
      //     };
      //   }
      //   return item;
      // });

      // console.log('activityTabBarList--setActivityItem----->', activityTabBarList.value);
    }
  };

  // 获取数据
  const getActivityDetail = async (forceRequest: boolean = false) => {
    const tabNum = activeTab.value;
    // 防重复请求
    if (!!activityDataMap.value[tabNum] && activityDataMap.value[tabNum].length && !forceRequest) {
      return;
    }

    if (!activityTabBarList.value.length) {
      return;
    }

    try {
      isLoading.value = true;
      const activityCategory = String(activityTabBarList.value[tabNum].id);
      const res = await activityServices.getActivityDetail({
        page_start: 1,
        num_per_page: 50,
        activityCategory,
      });
      if (res.type == NetworkTypeEnum.SUCCESS) {
        // @ts-ignore
        activityDataMap.value[tabNum] = res.data.data;
      }
    } finally {
      isLoading.value = false;
    }
  };

  const getActiveFormQuery = async (bulletinId: number) => {
    const res = await activityServices.getActiveFormQuery(bulletinId);

    if (res.type == NetworkTypeEnum.SUCCESS) {
      console.log(111111, res.data);
    }
  };

  // 获取活动详情
  const getActivityContentDetail = async params => {
    const res = await activityServices.getActivityContentDetail(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      // @ts-ignore
      activityContent.value = res.data.t;
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 申请自定义活动111
  const applyCustomActivity = async params => {
    const res = await activityServices.applyCustomActivity(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取活动支付类型
  const getPayOrgOptions = async () => {
    const res = await activityServices.getPayOrgOptions();
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取ip
  const getIp = async params => {
    const res = await activityServices.getIp(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };
  // 申请自定义活动点选验证
  const customizeActivityVerificationCode = async params => {
    const res = await activityServices.customizeActivityVerificationCode(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取自定义活动申请记录
  const getCustomActivityApplyRecord = async params => {
    const res = await activityServices.getCustomActivityApplyRecord(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取自定义活动可用次数111
  const getAvailablequantity = async params => {
    const res = await activityServices.getAvailablequantity(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取用户支付次数
  const getPayCount = async (params: any) => {
    const res = await activityServices.getPayCount(params);
    return res.data;
  };
  // 幸运注单活动详情
  const getLuckBetDetail = async params => {
    const res = await activityServices.getLuckBetDetail(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 幸运注单活动---查询用户可领取的总金额
  const checkLuckBetBlance = async () => {
    const res = await activityServices.checkLuckBetBlance();
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 查询幸运注单

  const reqLuckNotes = async params => {
    const res = await activityServices.reqLuckNotes(params);
    console.log(res, 'reqLuckNotesreqLuckNotes');
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };
  // 立即领取 幸运注单
  const getAward = async () => {
    const res = await activityServices.getAward();
    if (res.type == NetworkTypeEnum.SUCCESS) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 申请活动
  const activityApply = async params => {
    const res = await activityServices.activityApply(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      // @ts-ignore
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  };

  // 获取排行榜详情
  const getRankDetail = async params => {
    const res = await activityServices.getRankData(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      // @ts-ignore
      // rankDetail.value = res.data;
      // console.log('rankDetail.value', rankDetail.value)
    }
  };

  // 获取排行榜列表
  const getRankList = async params => {
    const res = await activityServices.getRankList(params);
    if (res.type == NetworkTypeEnum.SUCCESS) {
      // @ts-ignore
      rankDetail.value = res.data;
    }
  };

  // 救援金-领取奖励
  const reliefFundGet = async params => {
    const res = await activityServices.reliefFundGet(params);
    return res.data;
  };

  // 救援金-可领取奖励信息
  const getReliefFundInfo = async params => {
    const res = await activityServices.reliefFundInfo(params);
    return res.data;
  };

  // 领取新人彩金  - 用户兑换优惠码接口 可以有个公用的。
  const userExchangeCouponCode = async params => {
    const res = await activityServices.userExchangeCouponCode(params);
    return res.data;
  };

  // 领取新人彩金  - 用户获取优惠码接口 可以有个公用的。
  const userGetCouponCode = async params => {
    const res = await activityServices.userGetCouponCode(params);
    return res.data;
  };

  // 获取奖励
  const getAwardReq = async params => {
    const res = await activityServices.getAwardReq(params);
    return res.data;
  };

  // 获取打码活动奖励信息
  const getBetAwardInfo = async params => {
    const res = await activityServices.getBetAwardInfo(params);
    return res.data;
  };

  // 打码奖励领取
  const getBetAward = async params => {
    const res = await activityServices.getBetAward(params);
    return res.data;
  };
  // 获取充值活动奖励信息
  const getRechareInfo = async params => {
    const res = await activityServices.getRechareInfo(params);
    return res.data;
  };

  // 首页活动浮动轮播列表
  const getActiveActivityList = async (params?: any) => {
    const res = await activityServices.getActiveActivityList(params);
    return res.data;
  };

  // 获取千万福利图标
  const getWelfareIcons = async (params?: any) => {
    const res = await activityServices.getWelfareIcons(params);
    return res.data;
  };

  // 首页活动弹窗列表
  const getActiveActivityModalList = async (params?: any) => {
    const res = await activityServices.getActiveActivityModalList(params);
    return res.data;
  };

  // 获取红包配置信息
  const getHasRedPackets = async (params?: any) => {
    const res = await activityServices.getHasRedPackets(params);
    return res.data;
  };

  // 获取红包奖励
  const getRedPacketsAward = async (params?: any) => {
    const res = await activityServices.getRedPacketsAward(params);
    return res.data;
  };

  // 红包雨可领剩余次数
  const getRedPacketsRemainingCount = async (params?: any) => {
    const res = await activityServices.getRedPacketsRemainingCount(params);
    return res.data;
  };

  // 待领取-奖励领取
  const getWaitReceiveAward = async params => {
    const res = await activityServices.getWaitReceiveAward(params);
    return res.data;
  };

  // 待领取-一键领取奖励
  const getWaitReceiveAwardAll = async params => {
    const res = await activityServices.getWaitReceiveAwardAll(params);
    return res.data;
  };

  // 待领取-奖励领取
  const getWaitReceiveNum = async (params?: any) => {
    const res: any = await activityServices.getWaitReceiveNum(params);
    console.log(res, 'getWaitReceiveNum');
    waitReceiveNum.value = res?.data?.t || 0;
    return res.data;
  };

  // 查看是否有新的 预存 奖励可申请
  const getPrestoreReward = async (params?: any) => {
    const res: any = await activityServices.getPrestoreReward(params);
    return res.data;
  };
  // 预存奖励申请
  const prestoreRewardMoney = async (params: { tasks_id: any }) => {
    const res = await activityServices.prestoreRewardMoney(params);
    return res.data;
  };
  return {
    activityContent,
    rankDetail,
    activeTab,
    activityTabBarList,
    activityDataMap,
    isLoading,
    waitReceiveNum,
    changeActiveTab,
    getActivityData,
    getActivityDetail,
    getActiveFormQuery,
    getActivityContentDetail,
    activityApply,
    getRankDetail,
    getRankList,
    getAwardReq,
    reliefFundGet,
    getReliefFundInfo,
    userExchangeCouponCode,
    userGetCouponCode,
    changeActiveTabVO,
    getActivityDataVO,
    getActivityDetailVO,
    getCateActiveList,
    getBetAwardInfo,
    getBetAward,
    getRechareInfo,
    getActiveActivityList,
    getActiveActivityModalList,
    getHasRedPackets,
    getRedPacketsAward,
    getRedPacketsRemainingCount,
    activeTabVO,
    activityTabBarListVO,
    activityDataMapVO,
    getLuckBetDetail,
    checkLuckBetBlance,
    reqLuckNotes,
    getAward,
    getWaitReceiveAward,
    getWaitReceiveAwardAll,
    getWaitReceiveNum,
    applyCustomActivity,
    getIp,
    customizeActivityVerificationCode,
    getCustomActivityApplyRecord,
    getAvailablequantity,
    getPrestoreReward,
    prestoreRewardMoney,
    getPayOrgOptions,
    getPayCount,
    getWelfareIcons,
  };
});
